import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, RefreshCcw, Eye, Calendar, Users } from 'lucide-react';
import { Seo } from '../components/Seo';
import { BUSINESS, SEASON } from '../data/site';
import { SPEED_SCHOOL } from '../data/programs';
import { CHECKOUT, CANCEL_NOTICE, SIBLING_CODE } from '../data/checkout';
type TabId = 'monthly' | 'private' | 'single';
interface Plan {
  name: string;
  freq: string;
  ageNote?: string;
  price: string;
  priceNote: string;
  body?: string;
  savings?: string;
  features: string[];
  hero?: boolean;
  /** Stripe Payment Link. When present the card shows an Enroll button. */
  checkoutUrl?: string;
  ctaLabel?: string;
}
const TABS: {
  id: TabId;
  label: string;
}[] = [{
  id: 'monthly',
  label: 'Monthly'
}, {
  id: 'private',
  label: 'Private Training'
}, {
  id: 'single',
  label: 'Drop-In'
}];
const MONTHLY_PLANS: Plan[] = [{
  name: 'Advanced Performance',
  freq: '1x / week',
  ageNote: 'High School · Ages 14+ · Every sport',
  price: '$180',
  priceNote: '/ month',
  features: ['4 sessions per month', '$45 per session', '75-minute sessions', 'Tuesday or Thursday, 5:45 to 7:00 PM', 'Testing every 4 weeks'],
  checkoutUrl: CHECKOUT.advanced1x
}, {
  name: 'Advanced Performance',
  freq: '2x / week',
  ageNote: 'High School · Ages 14+ · Every sport',
  price: '$325',
  priceNote: '/ month',
  features: ['8 sessions per month', '$40.63 per session', '75-minute sessions', 'Tuesday and Thursday, 5:45 to 7:00 PM', 'Testing every 4 weeks'],
  hero: true,
  checkoutUrl: CHECKOUT.advanced2x
}, {
  name: 'Homeschool Multisport',
  freq: '1x / week',
  ageNote: 'Homeschool athletes · Capped at 10',
  price: '$225',
  priceNote: '/ month',
  features: ['90-minute sessions Tuesday and Thursday, 2:30 to 4:00 PM', 'Monday 10:00 AM start', 'Wednesday 12:00 PM start', 'Testing every 4 weeks'],
  checkoutUrl: CHECKOUT.homeschool1x
}, {
  name: 'Homeschool Multisport',
  freq: '2x / week',
  ageNote: 'Homeschool athletes · Capped at 10',
  price: '$400',
  priceNote: '/ month',
  features: ['90-minute sessions Tuesday and Thursday, 2:30 to 4:00 PM', 'Monday 10:00 AM start', 'Wednesday 12:00 PM start', 'Testing every 4 weeks'],
  checkoutUrl: CHECKOUT.homeschool2x
}];
const PRIVATE_1ON1: Plan[] = [{
  name: '1x / week',
  freq: 'Base Rate',
  price: '$600',
  priceNote: '/ month',
  features: ['4 sessions per month', '$150 per session', 'No discount'],
  checkoutUrl: CHECKOUT.private1x
}, {
  name: '2x / week',
  freq: 'Save 10%',
  price: '$1,080',
  priceNote: '/ month',
  savings: 'Save $120/mo',
  features: ['8 sessions per month', '$135 per session'],
  hero: true,
  checkoutUrl: CHECKOUT.private2x
}, {
  name: '3x / week',
  freq: 'Save 15%',
  price: '$1,530',
  priceNote: '/ month',
  savings: 'Save $270/mo',
  features: ['12 sessions per month', '$127.50 per session'],
  checkoutUrl: CHECKOUT.private3x
}];
const PRIVATE_2ON1: Plan[] = [{
  name: '1x / week',
  freq: 'Base Rate',
  price: '$500',
  priceNote: '/ month per athlete',
  features: ['4 sessions per month', '$125 per session', 'No discount'],
  checkoutUrl: CHECKOUT.semi1x
}, {
  name: '2x / week',
  freq: 'Save 10%',
  price: '$900',
  priceNote: '/ month per athlete',
  features: ['8 sessions per month', '$112.50 per session'],
  hero: true,
  checkoutUrl: CHECKOUT.semi2x
}, {
  name: '3x / week',
  freq: 'Save 15%',
  price: '$1,275',
  priceNote: '/ month per athlete',
  features: ['12 sessions per month', '$106.25 per session'],
  checkoutUrl: CHECKOUT.semi3x
}];
const SINGLE_PLANS: Plan[] = [{
  name: 'Drop-In',
  freq: 'Single session',
  price: '$50',
  priceNote: '/ session',
  body: 'One group session.',
  features: ['Try a group session', 'No commitment', 'Reserve the same week'],
  checkoutUrl: CHECKOUT.dropIn,
  ctaLabel: 'Buy a Drop-In'
}, {
  name: '10-Session Card',
  freq: '10 sessions',
  price: '$450',
  priceNote: 'total',
  body: '$45 per session, fully flexible.',
  features: [`Use through ${SEASON.end}`, 'Any group session', 'Share within family'],
  hero: true,
  checkoutUrl: CHECKOUT.card10,
  ctaLabel: 'Buy the 10-Session Card'
}, {
  name: 'Private 1-on-1',
  freq: 'Starting at',
  price: '$150',
  priceNote: '/ session',
  body: 'Final rate varies based on training frequency. The more sessions per week, the lower the per-session rate.',
  features: ['60 min with Coach Fernando', 'Custom programming', 'Position and sport-specific', 'Volume discounts available']
}];
const INCLUDED: string[] = ['Free athlete evaluation before the first session', 'Access to the on-site recovery zone', 'Periodized programming', 'Force plate and speed gate testing every 4 weeks', `10% sibling discount on additional athletes — use code ${SIBLING_CODE} at checkout`];
const PRIVATE_INCLUDED: string[] = ["Individualized program built around your athlete's sport and goals.", 'Direct coaching from Fernando Beltran, Director of Performance.', 'Performance testing every 4 weeks, same markers, real data.', 'Full access to the INPOWERiQ on-site recovery zone.', 'Speed and strength integrated into every session.', 'Make-ups allowed within the same week.'];
const POLICIES = [{
  Icon: RefreshCcw,
  title: 'Make-Ups',
  body: '1 free make-up per month in any group session.'
}, {
  Icon: Calendar,
  title: 'Cancellation',
  body: `Monthly plans renew automatically. Cancel anytime with ${CANCEL_NOTICE} notice before the next billing cycle.`
}, {
  Icon: Users,
  title: 'Holiday Closures',
  body: 'Closed Thanksgiving week, November 24 and 26. All other weeks run.'
}, {
  Icon: Eye,
  title: 'Parents Welcome',
  body: 'Observe any session from the parent gallery.'
}];
export function Pricing() {
  const [tab, setTab] = useState<TabId>('monthly');
  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({
    monthly: null,
    private: null,
    single: null
  });
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = TABS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = TABS[nextIndex].id;
    setTab(nextTab);
    tabRefs.current[nextTab]?.focus();
  };
  const plans = tab === 'monthly' ? MONTHLY_PLANS : SINGLE_PLANS;
  return <div className="w-full" data-id="element-738" data-mp="22e6ow5tsudyu">
      <Seo title="Training Rates and Private Coaching | Reach Performance McKinney" description="Monthly, private, and drop-in training rates for Reach Performance in McKinney, TX. Enroll online in a few taps, or book a free athlete evaluation to get started." path="/pricing" data-id="element-739" data-mp="22eyl9iv80fq6" />

      <section className="relative bg-reach-black overflow-hidden" data-id="element-740" data-mp="2lbga9sioq1iq">
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-741" data-mp="2lb7wlyeqms6h">
          <div className="absolute top-1/4 -left-40 w-[50%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-50" data-id="element-742" data-mp="2pbqqdy06y8bb" />
          <div className="absolute top-[42%] -left-40 w-[35%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-30" data-id="element-743" data-mp="2u4juqkrqzemv" />
          <div className="absolute top-[60%] -left-40 w-[25%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-20" data-id="element-744" data-mp="2z4pgjkbc66x1" />
        </div>
        <div className="max-w-container mx-auto px-4 md:px-8 py-20 md:py-24 text-center relative z-10" data-id="element-745" data-mp="390j19qz7l6pw">
          <p className="font-inter text-reach-red-light text-xs uppercase tracking-[0.3em] font-semibold mb-4" data-id="element-746" data-mp="3ub7u3zkqrpzx">
            {SEASON.label} · Enrollment open · Starts {SEASON.start}
          </p>
          <h1 className="font-bebas text-white text-6xl md:text-8xl tracking-wide leading-none mb-4" data-id="element-747" data-mp="77t6lfqezv5o">
            Training Rates
          </h1>
          <p className="font-inter text-white text-base md:text-xl max-w-2xl mx-auto" data-id="element-748" data-mp="28zthof1i661">
            Group training, private coaching, and drop-in options. Enroll online in a few taps, or
            book a free evaluation and we will confirm the right plan together.
          </p>
        </div>
      </section>

      <section className="bg-white sticky top-[112px] md:top-[136px] z-30 border-b border-black/5" data-id="element-749" data-mp="3kfwe7dxlkcql">
        <div className="max-w-container mx-auto px-4 md:px-8 py-5" data-id="element-750" data-mp="3fl08jazftqv0">
          <div className="flex justify-center" data-id="element-751" data-mp="2rw7i1u1l4b7s">
            <div role="tablist" aria-label="Pricing options" className="inline-flex bg-reach-gray rounded-[4px] p-1 w-full sm:w-auto" data-id="element-752" data-mp="2myfge8b51aqe">
              {TABS.map((item, index) => {
              const isActive = tab === item.id;
              return <button key={item.id} id={`pricing-tab-${item.id}`} ref={node => {
                tabRefs.current[item.id] = node;
              }} role="tab" type="button" aria-selected={isActive} aria-controls={`pricing-panel-${item.id}`} tabIndex={isActive ? 0 : -1} onClick={() => setTab(item.id)} onKeyDown={event => handleKeyDown(event, index)} className={`flex-1 sm:flex-none px-4 sm:px-6 py-2.5 min-h-[44px] rounded-[4px] font-inter text-xs sm:text-sm font-semibold uppercase tracking-wider transition-colors duration-200 ${isActive ? 'bg-reach-black text-white' : 'text-reach-black/80 hover:text-reach-black'}`} data-id="element-753" data-mp="tfylozdi8us6" data-mpi={index}>
                    {item.label}
                  </button>;
            })}
            </div>
          </div>
        </div>
      </section>

      {TABS.map(item => {
      if (item.id !== tab) return null;
      return <div key={item.id} role="tabpanel" id={`pricing-panel-${item.id}`} aria-labelledby={`pricing-tab-${item.id}`} tabIndex={0} className="focus:outline-none" data-id="element-754" data-mp="ffzwu7pw0p2x">
            {item.id === 'private' ? <PrivateTrainingTab data-id="element-755" data-mp="3np9wet0jy7v2" /> : <>
                <section className="bg-white py-16 md:py-20" data-id="element-756" data-mp="3snjtxa9r1kju">
                  <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-757" data-mp="3sntjwxusnrpj">
                    <h2 className="sr-only" data-id="element-758" data-mp="1ly5743m3djdw">
                      {item.id === 'monthly' ? 'Monthly training rates' : 'Drop-in options'}
                    </h2>

                    {item.id === 'monthly' && <p className="font-inter text-center text-sm text-reach-black/70 uppercase tracking-[0.2em] mb-8" data-id="element-759" data-mp="3beqy61ebn9v6">
                        High school groups run Tuesday and Thursday · Homeschool runs daytime blocks
                      </p>}

                    <div className="grid gap-5 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto" data-id="element-760" data-mp="3gd27mb0pficv">
                      {plans.map(plan => <PlanCard key={`${plan.name}-${plan.freq}`} plan={plan} data-id="element-761" data-mp="3u6jp7q9tyl09" />)}
                    </div>

                    {item.id === 'single' && <SpeedSchoolCard data-id="element-762" data-mp="7w7idyctxwh9" />}

                    <p className="font-inter text-center text-sm text-reach-black/70 mt-8 max-w-2xl mx-auto" data-id="element-763" data-mp="7v613c580dr3">
                      All prices in USD. Monthly plans are billed automatically each month and renew
                      until cancelled. Cancel anytime with {CANCEL_NOTICE} notice before the next
                      billing cycle. Secure checkout by Stripe.
                    </p>
                  </div>
                </section>

                <section className="bg-reach-gray py-20 md:py-24" data-id="element-764" data-mp="2ygkr1ujlnv49">
                  <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-765" data-mp="33evzq1y5rzkw">
                    <div className="text-center mb-10" data-id="element-766" data-mp="33gpbtwrjjc0a">
                      <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-767" data-mp="38eqrs8to6v7j">
                        Every Plan Includes
                      </p>
                      <h2 className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-768" data-mp="3hdlz5o8gabcf">
                        What's Included
                      </h2>
                    </div>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white rounded-[4px] p-6 md:p-8 border-l-4 border-reach-red" data-id="element-769" data-mp="3r4qe4ny4lw79">
                      {INCLUDED.map(entry => <li key={entry} className="flex items-start gap-3" data-id="element-770" data-mp="4wqrzgy9pw50">
                          <div className="w-6 h-6 bg-reach-red rounded-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-771" data-mp="4umeq8ehbks2">
                            <Check size={14} className="text-white" strokeWidth={3} aria-hidden="true" data-id="element-772" data-mp="1kg7ux9d3dh2u" />
                          </div>
                          <span className="font-inter text-reach-black text-base" data-id="element-773" data-mp="1fhmw9edhn5gi">{entry}</span>
                        </li>)}
                    </ul>
                  </div>
                </section>

                <section className="bg-white py-16" data-id="element-774" data-mp="284r21lkenntc">
                  <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-775" data-mp="23662locz985y">
                    <h2 className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-6 text-center" data-id="element-776" data-mp="1yfpp6495pmjl">
                      Policies
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto" data-id="element-777" data-mp="1tes8jw3sp0k7">
                      {POLICIES.map(({
                  Icon,
                  title,
                  body
                }) => <div key={title} className="flex items-start gap-3" data-id="element-778" data-mp="2m3z6otzc7o4x">
                          <Icon size={18} className="text-reach-red shrink-0 mt-1" aria-hidden="true" data-id="element-779" data-mp="26m93gve6pnf1" />
                          <div data-id="element-780" data-mp="2bn514r7y0dc0">
                            <h3 className="font-bebas text-lg tracking-wider uppercase mb-1" data-id="element-781" data-mp="2bkaldczvjgox">
                              {title}
                            </h3>
                            <p className="font-inter text-sm text-reach-black/75 leading-relaxed" data-id="element-782" data-mp="21oz3ilt65tcy">
                              {body}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </section>
              </>}
          </div>;
    })}

      <section className="bg-reach-red py-16 md:py-20" data-id="element-783" data-mp="2km08i6mq3tgw">
        <div className="max-w-container mx-auto px-4 md:px-8 text-center" data-id="element-784" data-mp="2kkp90j0b97vl">
          <h2 className="font-bebas text-white text-5xl md:text-7xl tracking-wide mb-4" data-id="element-785" data-mp="38bbeq3mueg2f">
            Ready To Start
          </h2>
          <p className="font-inter text-white text-lg md:text-xl mb-10 max-w-2xl mx-auto" data-id="element-786" data-mp="11odoia8h5btr">
            Not sure which plan fits? Book a free athlete evaluation and we will confirm it together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center max-w-md sm:max-w-none mx-auto" data-id="element-787" data-mp="1bdxahorhhlsu">
            <Link to="/book" className="inline-flex items-center justify-center bg-white text-reach-black font-inter font-semibold px-6 min-h-[44px] md:min-h-[48px] rounded-[4px] transition-all duration-200 ease-out hover:bg-reach-black hover:text-white uppercase tracking-wide text-sm md:text-base hover:-translate-y-[3px]" data-id="element-788" data-mp="16gey1oe9cog3">
              Book Free Athlete Evaluation
            </Link>
            <Link to="/contact" className="btn-secondary-dark sm:w-auto" data-id="element-789" data-mp="nqp7xelfwqui">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>;
}

/* ---------- Speed School card ---------- */
function SpeedSchoolCard() {
  return <div className="max-w-6xl mx-auto mt-6 border-l-4 border-reach-red bg-reach-gray p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5" data-id="element-790" data-mp="vrspb5fm2xjs">
      <div data-id="element-791" data-mp="15rb5dasehz6k">
        <p className="font-inter text-reach-red text-xs uppercase tracking-[0.25em] font-semibold mb-2" data-id="element-792" data-mp="15qi3an4dzxur">
          {SPEED_SCHOOL.eyebrow}
        </p>
        <h3 className="font-bebas text-2xl md:text-3xl tracking-wide mb-2" data-id="element-793" data-mp="19qivrbobm9kt">Speed School</h3>
        <p className="font-inter text-base text-reach-black/80 max-w-xl" data-id="element-794" data-mp="d477dprq6l4a">
          {SPEED_SCHOOL.bothSessions}, {SPEED_SCHOOL.dropIn}, {SPEED_SCHOOL.cap}. Reserve online, or
          text {BUSINESS.phoneDisplay}.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-795" data-mp="qtir0lylbgty">
        <a href={CHECKOUT.speedSchoolBoth} className="btn-primary" data-id="element-796" data-mp="qv2asmjgga0h">
          Reserve Both · $125
        </a>
        <a href={CHECKOUT.speedSchoolSingle} className="btn-secondary" data-id="element-797" data-mp="3qffzepwldgel">
          Single · $75
        </a>
      </div>
    </div>;
}

/* ---------- Private Training Tab ---------- */
function PrivateTrainingTab() {
  return <>
      <section className="bg-white py-16 md:py-20" data-id="element-798" data-mp="3hzqhvrjmvmlm">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-799" data-mp="3hy5klu5xppk2">
          <div className="mb-16" data-id="element-800" data-mp="3dxlpew4x8w0k">
            <div className="text-center mb-8" data-id="element-801" data-mp="3e0rdq95q7tv8">
              <h2 className="font-bebas text-4xl md:text-5xl tracking-wide mb-2" data-id="element-802" data-mp="390d44moh8bv3">
                1-on-1 Private Coaching
              </h2>
              <p className="font-inter text-reach-black/75 text-base" data-id="element-803" data-mp="341s1yhpnzi40">
                Base rate $150 per session
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto" data-id="element-804" data-mp="2ua5umwgg636j">
              {PRIVATE_1ON1.map(plan => <PlanCard key={`1on1-${plan.name}-${plan.freq}`} plan={plan} data-id="element-805" data-mp="2qancu9jxa87x" />)}
            </div>
          </div>

          <div data-id="element-806" data-mp="2lbtua2ps3r0w">
            <div className="text-center mb-8" data-id="element-807" data-mp="2gb67jt8l1wch">
              <h2 className="font-bebas text-4xl md:text-5xl tracking-wide mb-2" data-id="element-808" data-mp="2gbektm8mb3o7">
                2-on-1 Semi-Private
              </h2>
              <p className="font-inter text-reach-black/75 text-base" data-id="element-809" data-mp="80devhgyvtr4">
                Base rate $125 per athlete per session · each athlete enrolls separately
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto" data-id="element-810" data-mp="3v98qhhe2v4rq">
              {PRIVATE_2ON1.map(plan => <PlanCard key={`2on1-${plan.name}-${plan.freq}`} plan={plan} data-id="element-811" data-mp="3lgurgwedwc99" />)}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-reach-gray py-16 md:py-20" data-id="element-812" data-mp="3q9nv1gy49ejr">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-813" data-mp="3chpp1ezpbyrt">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-814" data-mp="3ghqdlsgilqcl">
            <div className="bg-white rounded-[4px] p-8 border-l-4 border-reach-red" data-id="element-815" data-mp="32i8lb340fuyh">
              <h2 className="font-bebas text-2xl md:text-3xl tracking-wide mb-4" data-id="element-816" data-mp="32kl2he1y21ah">
                Who 1-on-1 is for
              </h2>
              <p className="font-inter text-reach-black text-base leading-relaxed" data-id="element-817" data-mp="2nv08xyyytjkx">
                Coming off injury. In-season position work. Recruiting-stage athletes who need every
                rep to count. Collegiate and pro prep. Anyone who wants the fastest possible progress
                with full coach attention.
              </p>
            </div>
            <div className="bg-white rounded-[4px] p-8 border-l-4 border-reach-red" data-id="element-818" data-mp="2swz96zzes0dn">
              <h2 className="font-bebas text-2xl md:text-3xl tracking-wide mb-4" data-id="element-819" data-mp="36sb3x7dkdquk">
                Who 2-on-1 is for
              </h2>
              <p className="font-inter text-reach-black text-base leading-relaxed" data-id="element-820" data-mp="3gjxnps3ywy8x">
                Siblings. Training partners. Teammates. Private-level coaching at a lower per-athlete
                rate. Both athletes commit to the same schedule and frequency.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20" data-id="element-821" data-mp="3lkv3xz5f3i7s">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-822" data-mp="1wdxlq161tr0">
          <div className="text-center mb-10" data-id="element-823" data-mp="1ut3u2moeeu7">
            <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-824" data-mp="3t3gnme59ptzn">
              Every Private Membership Includes
            </p>
            <h2 className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-825" data-mp="auy9a78b735q">
              What's Included
            </h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-reach-gray rounded-[4px] p-6 md:p-8 border-l-4 border-reach-red" data-id="element-826" data-mp="3ef2mq8rewflq">
            {PRIVATE_INCLUDED.map(item => <li key={item} className="flex items-start gap-3" data-id="element-827" data-mp="3o4dqdfvj4s28">
                <div className="w-6 h-6 bg-reach-red rounded-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-828" data-mp="3o5ozlv7qus0q">
                  <Check size={14} className="text-white" strokeWidth={3} aria-hidden="true" data-id="element-829" data-mp="2vn9u4fg98wej" />
                </div>
                <span className="font-inter text-reach-black text-base" data-id="element-830" data-mp="2qp8c7xukf34n">{item}</span>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-reach-gray py-12" data-id="element-831" data-mp="164l1r1hf9qd8">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-832" data-mp="1ay6393i852h6">
          <p className="font-inter text-reach-black/75 text-sm leading-relaxed text-center" data-id="element-833" data-mp="x5pvxqiaiiag">
            Monthly memberships renew each month. Cancel anytime with {CANCEL_NOTICE} notice before
            the next billing cycle. Missed sessions can be made up within the same week with 24 hour
            notice. 2-on-1 pricing is per athlete and both athletes are billed separately. The
            sibling discount stacks on 2-on-1 packages when both athletes are siblings. Private
            sessions run 60 or 75 minutes depending on age and goals.
          </p>
        </div>
      </section>
    </>;
}

/* ---------- Subcomponents ---------- */
function PlanCard({
  plan
}: {
  plan: Plan;
}) {
  const isDark = !!plan.hero;
  return <div className={`relative rounded-[4px] p-6 md:p-7 flex flex-col transition-transform duration-200 ease-out overflow-hidden border-b-4 ${isDark ? 'bg-reach-black text-white ring-2 ring-reach-red border-transparent' : 'bg-reach-gray text-reach-black border-transparent hover:-translate-y-1'}`} data-id="element-834" data-mp="9gf0mojplg3p">
      <div className="mt-2" data-id="element-835" data-mp="3cptnv4x73rx6">
        <p className={`font-inter text-xs uppercase tracking-[0.25em] font-semibold mb-2 ${isDark ? 'text-reach-red-light' : 'text-reach-red'}`} data-id="element-836" data-mp="3cp0ctrr7eg98">
          {plan.freq}
        </p>
        <h3 className="font-bebas text-2xl md:text-3xl tracking-wide leading-none mb-2" data-id="element-837" data-mp="32quc3dh4vg5y">
          {plan.name}
        </h3>
        {plan.ageNote && <p className={`font-inter text-xs uppercase tracking-wider mb-5 ${isDark ? 'text-white/70' : 'text-reach-black/70'}`} data-id="element-838" data-mp="2trh30lwx8tlr">
            {plan.ageNote}
          </p>}
        {plan.body && <p className={`font-inter text-sm mb-5 leading-relaxed ${isDark ? 'text-white/85' : 'text-reach-black/80'}`} data-id="element-839" data-mp="s0dqts7dv6z3">
            {plan.body}
          </p>}
      </div>

      <div className="mb-6 flex items-baseline gap-2 flex-wrap" data-id="element-840" data-mp="uksgmha3ld2z">
        <span className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-841" data-mp="zjmxhdoi8rkg">
          {plan.price}
        </span>
        <span className={`font-inter text-sm ${isDark ? 'text-white/75' : 'text-reach-black/70'}`} data-id="element-842" data-mp="14cg6idr0am2c">
          {plan.priceNote}
        </span>
        {plan.savings && <span className={`ml-auto inline-block font-inter text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-[4px] ${isDark ? 'bg-white text-reach-red' : 'bg-reach-red text-white'}`} data-id="element-843" data-mp="bn96197zrmeg">
            {plan.savings}
          </span>}
      </div>

      <ul className="space-y-2.5 mb-7 flex-grow" data-id="element-844" data-mp="plz0eh3ff7z4">
        {plan.features.map(feature => <li key={feature} className={`flex items-start gap-2.5 text-sm ${isDark ? 'text-white/85' : 'text-reach-black/85'}`} data-id="element-845" data-mp="1x02h45yniqyp">
            <Check size={16} className={`shrink-0 mt-0.5 ${isDark ? 'text-reach-red-light' : 'text-reach-red'}`} aria-hidden="true" data-id="element-846" data-mp="20zku8fk8dxqz" />
            <span data-id="element-847" data-mp="1umozeso8n2da">{feature}</span>
          </li>)}
      </ul>

      {plan.checkoutUrl ? <div className="flex flex-col gap-2" data-id="element-848" data-mp="29jzeec03aqdr">
          <a href={plan.checkoutUrl} className="btn-primary w-full" data-id="element-849" data-mp="24jl6qv2ehig9">
            {plan.ctaLabel ?? 'Enroll Now'}
            <ArrowRight size={16} className="ml-2" aria-hidden="true" data-id="element-850" data-mp="2iib50e0yho67" />
          </a>
          <Link to="/book" className={`w-full text-center font-inter text-xs font-semibold uppercase tracking-wider py-2 transition-colors ${isDark ? 'text-white/70 hover:text-white' : 'text-reach-black/65 hover:text-reach-black'}`} data-id="element-851" data-mp="2dk9jlmg25crk">
            Or book a free evaluation first
          </Link>
        </div> : <Link to="/book" className={isDark ? 'btn-primary w-full' : 'btn-secondary w-full'} data-id="element-852" data-mp="319u7mjr2j07p">
          Book Free Athlete Evaluation
          <ArrowRight size={16} className="ml-2" aria-hidden="true" data-id="element-853" data-mp="3fw9mzemyi0gb" />
        </Link>}
    </div>;
}